import { supabase } from '../config/supabase';

export interface UserReleaseAcknowledgement {
  userId: string;
  releaseId: string;
  firstSeenAt: string;
  lastViewedAt: string;
  completedAt: string | null;
}

interface AcknowledgementRow {
  userId: string;
  releaseId: string;
  firstSeenAt: string;
  lastViewedAt: string;
  completedAt: string | null;
}

export class UserReleaseAcknowledgementService {
  static async listForUser(userId: string): Promise<UserReleaseAcknowledgement[]> {
    const { data, error } = await supabase
      .from('user_release_acknowledgements')
      .select('userId, releaseId, firstSeenAt, lastViewedAt, completedAt')
      .eq('userId', userId)
      .order('firstSeenAt', { ascending: false });

    if (error) throw error;
    return (data || []) as AcknowledgementRow[];
  }

  static async markViewed(userId: string, releaseId: string): Promise<void> {
    const now = new Date().toISOString();
    const { error } = await supabase
      .from('user_release_acknowledgements')
      .upsert(
        {
          userId,
          releaseId,
          lastViewedAt: now,
        },
        { onConflict: 'userId,releaseId' },
      );

    if (error) throw error;
  }

  static async markCompleted(userId: string, releaseId: string): Promise<void> {
    const now = new Date().toISOString();
    const { data: existing, error: readError } = await supabase
      .from('user_release_acknowledgements')
      .select('firstSeenAt, lastViewedAt, completedAt')
      .eq('userId', userId)
      .eq('releaseId', releaseId)
      .maybeSingle();

    if (readError) throw readError;

    // O default firstSeenAt do banco pode ser posterior ao relógio do navegador.
    // Envie datas consistentes também na primeira sincronização de uma leitura offline.
    const firstSeenAt = existing?.firstSeenAt ?? now;
    const completedAt = existing?.completedAt ?? new Date(
      Math.max(Date.parse(now), Date.parse(firstSeenAt)),
    ).toISOString();
    const lastViewedAt = new Date(Math.max(
      Date.parse(now), Date.parse(firstSeenAt), Date.parse(completedAt),
      Date.parse(existing?.lastViewedAt ?? now),
    )).toISOString();
    const { error } = await supabase
      .from('user_release_acknowledgements')
      .upsert(
        {
          userId,
          releaseId,
          firstSeenAt,
          lastViewedAt,
          completedAt,
        },
        { onConflict: 'userId,releaseId' },
      );

    if (error) throw error;
  }
}
